import React, { useState, useEffect, useCallback } from 'react';
import ContentDividerWithIcon from '../../components/ContentDividerWithIcon';
import CartSumInfo from '../../components/CartSumInfo';
import CheckoutSubheader from '../../components/CheckoutSubheader';
import { useSelector, useDispatch } from 'react-redux';
import { postToHub } from '../../actions/networkActions';
import If from '../../components/If';
import TextInputWithLabel from '../../components/TextInputWithLabel';
import { updateBookings, updateCart } from '../../actions/cartActions';
import { themableImagePath } from '../../utils/imageUtils';
import { savePayment as savePaymentAction } from '../../actions/paymentActions';
import CartElementsCheckoutCombined from "../../components/cartElements/CartElementsCheckoutCombined";
import ErrorModal from "../../components/ErrorModal";
import { isEmail } from "../../utils/validationUtils";
import BackLink from "../../components/BackLink";
import { skipPayment } from "../../utils/configUtils";
import ThemableComponent from '../../components/theme/ThemableComponent';
import { Trans, useLingui } from '@lingui/react/macro';
import { navigate } from '../../utils/navigationUtils';

function PassengerDetails() {
    const isAgency = useSelector(state => state.agency.isAuthenticated);
    const dispatch = useDispatch();

    const [paymentReference, setPaymentReference] = useState("");
    const [email, setEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [importantInfoChecked, setImportantInfoChecked] = useState(false);
    const [errors, setErrors] = useState({});
    const [isCpsEnabled, setIsCpsEnabled] = useState(false);
    const [continueButtonDisabled, setContinueButtonDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [payment, setPayment] = useState(null);

    const {t} = useLingui();

    useEffect(() => {
        updateCart();
    }, []);

    const toggleImportantInfoCheckbox = () => {
        setImportantInfoChecked(!importantInfoChecked);
    };

    const processForm = (e) => {
        e.preventDefault();
        if (continueButtonDisabled) {
            return;
        }
        const shouldSkipPayment = skipPayment();
        let newErrors = {};
        if (!isEmail(email)) {
            newErrors.email = t({id: "please_enter_email_address"});
        }
        if (email !== confirmEmail) {
            newErrors.confirm = t({id: "email_addresses_must_match"});
        }
        if (!importantInfoChecked) {
            newErrors.importantInfo = t({id: "please_read_and_agree_to_terms_and_conditions"});
        }
        if (isAgency && !paymentReference) {
            newErrors.paymentReference = "Please enter the payment reference";
        }
        const regex = RegExp('^[a-z0-9]{4,8}$', 'i');
        if (isAgency && !!paymentReference && !regex.test(paymentReference)) {
            newErrors.paymentReference = "Please enter a valid reference containing between 4-8 characters, and no punctuation or special characters.";
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setContinueButtonDisabled(true);
        if (isAgency || shouldSkipPayment) {
            order();
        } else {
            retrieveAndActivateCpsPayment();
        }
    };

    const retrieveAndActivateCpsPayment = () => {
        let paymentData = {
            confirmationMail: confirmEmail,
            paymentReference: paymentReference,
            mobileNumber: mobileNumber,
            tomcatInstance: undefined
        };
        const clientDomainUrl = window.location.protocol + "//" + window.location.host;
        let data = {
            clientDomainUrl: clientDomainUrl
        };
        postToHub("cpspayment/paymentUrl", data, (response) => {
            if (response.isCpsEnabled) {
                setIsCpsEnabled(true);
                if (response.token) {
                    paymentData.paymentReference = response.token;
                    paymentData.tomcatInstance = response.tomcatInstance;
                    dispatch(savePaymentAction(paymentData));
                }
                if (response.redirectUrl) {
                    window.location.replace(response.redirectUrl);
                }
            } else {
                setIsCpsEnabled(false);
                order();
            }
        }, (error) => {
            console.error(error);
            setPayment({
                networkError: "An error has occurred! Please check your network connection."
            });
            setContinueButtonDisabled(false);
        });
    };

    const order = () => {
        let request = {
            confirmationMail: confirmEmail,
            paymentReference: paymentReference,
            mobileNumber: mobileNumber
        };
        postToHub("prebooking/order?bookingSource=webApp", request, onOrderCompleted, onOrderError);
    };

    const onOrderCompleted = (response) => {
        if (isAgency || !isCpsEnabled) {
            navigate(t({id: "/confirmation/b2b"}), {
                state: {
                    bookingNr: response.groupReference,
                    logoutUserOnMount: isAgency,
                    confirmationMail: email,
                    bookingParts: response.bookingParts,
                },
            });
        } else {
            setContinueButtonDisabled(false);
            updateCart();
            updateBookings();
        }
    };

    const onOrderError = (response) => {
        const conflictErrorResponses = [
            "WaitlistedBooking",
            "BookingValidationCheckResult != Bookable",
            "Shoppingcart NOT bookable",
        ];
        const isConflictErrorResponse = conflictErrorResponses.includes(response.response?.data);
        if (isConflictErrorResponse) {
            updateCart();
        }
        const msg = isConflictErrorResponse ?
            "" :
            t({id:"error_when_ordering_try_again"});
        setContinueButtonDisabled(false);
        setErrorMessage(msg);
    };

    return (
        <div>
            <div className="bg-primary text-white uppercase">
                <CheckoutSubheader
                    active="summary"
                    hideOnMobile={false}
                />
            </div>
            <div className="container px-4 mx-auto mt-4">
                <BackLink to={t({id: "/itinerary"})} text={t({id: "cruise_planner"})} />
                <h1><Trans id="summary">Summary</Trans></h1>
                <div className="shadow">
                    <ContentDividerWithIcon
                        iconAltText="Shopping Cart"
                        iconSrc={themableImagePath('shoppingcart.svg')}
                        title={t({id: "your_cruise_items"})} 
                    />
                    <CartElementsCheckoutCombined />
                </div>
                <form className="mt-8">
                    <If test={isAgency}>
                        <ContentDividerWithIcon
                            iconAltText="tui logo"
                            iconSrc={themableImagePath('logo.svg')}
                            title="Agency Information" />
                        <div className="px-2 sm:px-4 mb-8">
                            <div className="my-4">
                                <TextInputWithLabel
                                    label="Payment Reference"
                                    type="text"
                                    value={paymentReference}
                                    _onChange={setPaymentReference}
                                    placeholder=""
                                    errorMessage={errors.paymentReference}
                                />
                            </div>
                        </div>
                    </If>
                    <ContentDividerWithIcon
                        iconAltText="email at sign"
                        iconSrc={themableImagePath('at-symbol.svg')}
                        title={t({id:"contact_details"})} />
                    {/*contact fields*/}
                    <div className="px-2 sm:px-4 mb-8">
                        <div className="my-4">
                            <TextInputWithLabel
                                label={isAgency ? t({id:"customer_email_address"}) : t({id:"email_address"})}
                                type="text"
                                value={email}
                                _onChange={setEmail}
                                placeholder=""
                                errorMessage={errors.email}
                            />
                        </div>
                        <div className="my-4">
                            <TextInputWithLabel
                                label={isAgency ? t({id:"confirm_customer_email_address"}) : t({id:"confirm_email_address"})}
                                type="text"
                                value={confirmEmail}
                                _onChange={setConfirmEmail}
                                placeholder=""
                                errorMessage={errors.confirm}
                            />
                        </div>
                        <div className="my-4 text-grey-darker">
                            <TextInputWithLabel
                                label={isAgency ? t({id:"customer_mobile_number_optional"}) : t({id:"mobile_number_optional"})}
                                type="text"
                                value={mobileNumber}
                                _onChange={setMobileNumber}
                                placeholder=""
                            />
                            <Trans id="enter_mobile_number_to_keep_you_informed">Please enter a mobile number if you have one so we can keep you informed whilst you are on holiday</Trans>
                        </div>
                    </div>
                    {/* terms & conditions */}
                    <ThemableComponent
                        componentName="TermsAndConditionsCheckbox"
                        importantInfoChecked={importantInfoChecked}
                        handleCheckboxChange={toggleImportantInfoCheckbox}
                        errorMessage={errors.importantInfo}
                    />
                    {Array.isArray(errors) && errors.length > 0 &&
                        <div className="container my-4 errors">
                            {errors.map((error, index) => <p key={index}>{error}</p>)}
                        </div>
                    }
                    {/*cost sum and continue button*/}
                    <CartSumInfo onClick={processForm} inProgress={continueButtonDisabled} />
                    {errors.networkError &&
                        <div className="errors">{errors.networkError}</div>
                    }
                    {payment &&
                        <div className="container my-8 flex"><div className="errors">{payment.networkError}</div></div>
                    }
                </form>
            </div>
            <ErrorModal
                show={errorMessage !== ""}
                onClose={() => setErrorMessage("")}
                title={t({id:"error"})}
                message={errorMessage}
            />
        </div>
    );
}

export default PassengerDetails;

